export type SuperTab =
  | 'overview'
  | 'analytics'
  | 'tenants'
  | 'landing'
  | 'voice'
  | 'plans'
  | 'broadcasts'
  | 'platform'
  | 'audit'
  | 'system';

export type TenantDomain = {
  id: string;
  host: string;
  is_primary: boolean;
  is_active: boolean;
};

export type TenantUser = {
  id: string;
  name: string;
  email: string;
  role: string;
  is_active: boolean;
  password_setup_required: boolean;
};

export type TenantVoice = {
  id: string;
  did_number: string;
  provider: string;
  is_active: boolean;
};

export type TenantFeatures = {
  public_ordering: boolean;
  table_service: boolean;
  voice_room_assistant: boolean;
  pwa_install: boolean;
  events: boolean;
  voice_agent: boolean;
  /** Demo quick-access buttons on tenant auth / PWA / mesa / waiter (not super-admin). */
  demo_quick_access: boolean;
  /** CRM de clientes, fidelizacao, auth nas PWAs e personalizacao de voz. */
  crm: boolean;
};

export type TenantRow = {
  id: string;
  name: string;
  slug: string;
  status: string;
  phone: string | null;
  email: string | null;
  city: string | null;
  settings?: Record<string, unknown> | null;
  communications_account_id: string | null;
  plan_slug?: string;
  landing_visible: boolean;
  landing_order: number;
  domains: TenantDomain[];
  users: TenantUser[];
  voice_numbers: TenantVoice[];
  order_count: number;
  orders_7d: number;
};

export type PlatformOverview = {
  tenants_active: number;
  tenants_total: number;
  tenant_users: number;
  platform_admins: number;
  domains_active: number;
  voice_numbers_active: number;
  orders_24h: number;
  orders_7d: number;
  revenue_7d: number;
};

export type PlatformAdminUser = {
  id: string;
  name: string;
  email: string;
  role: string;
  is_active: boolean;
  last_login_at: string | null;
  created_at: string;
};

export type PlatformHealth = {
  database: { ok: boolean; latency_ms: number };
  voice: { ok: boolean; latency_ms: number; endpoint: string };
  environment: { node_env: string; app_url: string | null };
  checked_at: string;
};

export type RecentOrder = {
  id: string;
  order_number: string;
  total: number;
  status: string;
  channel: string;
  created_at: string;
  tenant_name: string;
  tenant_slug: string;
};

export type RecentLogin = {
  name: string;
  email: string;
  last_login_at: string;
};

export type RecentActivity = {
  orders: RecentOrder[];
  logins: RecentLogin[];
};

export type PlatformAlert = {
  id: string;
  severity: 'critical' | 'warning' | 'info';
  title: string;
  detail: string;
  href?: string;
};

export type AnalyticsData = {
  days: number;
  daily: Array<{ day: string; orders: number; revenue: number }>;
  by_tenant: Array<{ tenant_name: string; tenant_slug: string; orders: number; revenue: number }>;
};

export type AuditEntry = {
  id: string;
  actor_email: string;
  action: string;
  entity_type: string | null;
  entity_id: string | null;
  tenant_id: string | null;
  tenant_name: string | null;
  details: Record<string, unknown>;
  created_at: string;
};

export type PlanRow = {
  id: string;
  name: string;
  slug: string;
  description: string;
  price: number;
  billing_cycle: string;
  features: unknown;
  limits: unknown;
  tenant_count: number;
};

export type BroadcastRow = {
  id: string;
  title: string;
  body: string;
  audience: string;
  created_by_email: string;
  sent_at: string;
};

export type IntegrationService = {
  name: string;
  ok: boolean;
  detail: string;
};

export const FEATURE_LABELS: Record<keyof TenantFeatures, string> = {
  public_ordering: 'Pedidos online',
  table_service: 'Servico de mesa',
  voice_room_assistant: 'Assistente de sala',
  pwa_install: 'PWA / Instalar app',
  events: 'Eventos',
  voice_agent: 'Agente de voz',
  demo_quick_access: 'Acessos demo (botoes rapidos)',
  crm: 'CRM / Fidelizacao / Auth cliente',
};

export function parseTenantFeatures(settings?: Record<string, unknown> | null): TenantFeatures {
  const f = (settings?.features ?? settings ?? {}) as Partial<TenantFeatures>;
  return {
    public_ordering: f.public_ordering !== false,
    table_service: f.table_service !== false,
    voice_room_assistant: f.voice_room_assistant !== false,
    pwa_install: f.pwa_install !== false,
    events: f.events !== false,
    voice_agent: f.voice_agent !== false,
    demo_quick_access: f.demo_quick_access === true,
    crm: f.crm === true,
  };
}
